import { isEmpty } from 'lodash';

import { ButtonLabel } from '@/components/text/Texts';
import { BACK, CONTINUE_WITH_GOOGLE, NEXT } from '@/constants/button-labels';
import IdGeneratorService from '@/services/id-generator.service';
import clsx from 'clsx';
import { Button, type ButtonProps } from 'primereact/button';
import { classNames } from 'primereact/utils';
import type { ButtonHTMLAttributes, ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { MdAdd, MdArrowBack, MdArrowForward } from 'react-icons/md';
import Container from '../container/Container';
import styles from './buttons.module.css';
import './buttons.styles.css';
import { cn } from '../ui/cn';

type Props = {
	children?: ReactNode;
	id: string;
	className?: string;
	formName?: string;
	disabled?: boolean | false;
	label?: string;
	onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void;
	isActive?: boolean;
	icon?: IconType;
	trailingIcon?: IconType;
	iconColor?: string;
	iconSize?: string;
	width?: string;
	withIcon?: boolean;
	type?: 'submit' | 'reset' | 'button';
	href?: string;
	newTab?: boolean;
	thin?: boolean;
	clear?: boolean;
};

interface CustomButtonProps extends Partial<ButtonProps> {
	id: string;
	formName?: string;
	type?: 'submit' | 'reset' | 'button';
}

export const TextButton = ({
	label,
	id,
	formName = '',
	className,
	href = 'https://react.dev',
	newTab = false,
}: Props) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<a
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			href={href}
			target={newTab ? '_blank' : '_self'}
			rel="noopener noreferrer"
			className={`p-button font-bold ${className}`}
		>
			{label || idService.generateLabel(id)}
		</a>
	);
};

export const TextOnlyButton = ({
	children,
	id,
	label,
	onClick,
	className,
	formName = '',
}: Props) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className={clsx(className, styles.textOnlyButton)}
			onClick={onClick}
			text
		>
			{children || label || idService.generateLabel(id)}
		</Button>
	);
};

export const RoundedFilledButton = ({
	id,
	label,
	onClick,
	disabled,
	className,
	icon: LeadingIcon,
	trailingIcon: TrailingIcon,
	iconColor,
	iconSize,
	width,
	height,
	type = 'button',
	thin,
	clear,
	formName = '',
}: Props & { icon?: IconType; width?: string; height?: string }) => {
	iconSize = iconSize || '18';
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className={cn(
				"bg-primary text-white font-medium text-label-large justify-center font-poppins h-[40px] rounded-[100px] px-6",
				className,
				thin && styles.thinButton,
				clear && styles.clear,
				'flex items-center justify-center gap-2',
			)}
			rounded
			style={{ width, height }}
			disabled={disabled}
			onClick={onClick}
			type={type}
		>
			{LeadingIcon && (
				<LeadingIcon size={iconSize} className={`${iconColor} `} />
			)}
			{label || idService.generateLabel(id)}
			{TrailingIcon && (
				<TrailingIcon size={iconSize} className={`${iconColor} `} />
			)}
		</Button>
	);
};

export const RoundedOutlinedFilledButton = ({
	id,
	label,
	onClick,
	disabled,
	className,
	icon: LeadingIcon,
	trailingIcon: TrailingIcon,
	iconColor,
	iconSize,
	width,
	height,
	type = 'button',
	formName = '',
}: Props & { icon?: IconType; width?: string; height?: string }) => {
	iconSize = iconSize || '18';
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className={cn('flex items-center justify-center gap-2  h-[40px] rounded-[100px] px-6', className)}
			rounded
			style={{ width, height }}
			disabled={disabled}
			onClick={onClick}
			type={type}
			outlined
		>
			{LeadingIcon && (
				<LeadingIcon size={iconSize} className={`${iconColor} `} />
			)}
			{label || idService.generateLabel(id)}
			{TrailingIcon && (
				<TrailingIcon size={iconSize} className={`${iconColor} `} />
			)}
		</Button>
	);
};

export const AddButton = ({
	id,
	className,
	icon,
	...props
}: Partial<Props> & { id: string }) => {
	return (
		<RoundedFilledButton
			id={id}
			icon={icon ?? MdAdd}
			className={`add-button ${className}`}
			{...props}
		/>
	);
};

export const RoundedIconButton = ({
	id,
	onClick,
	disabled,
	className,
	icon: LeadingIcon,
	trailingIcon: TrailingIcon,
	iconColor,
	iconSize,
	width,
	type = 'button',
	formName = '',
}: Props & { icon?: IconType; width?: string }) => {
	iconSize = iconSize || '18';
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className={`${className} flex items-center justify-center gap-2`}
			rounded
			style={{ width }}
			disabled={disabled}
			onClick={onClick}
			type={type}
		>
			{LeadingIcon && (
				<LeadingIcon size={iconSize} className={`${iconColor} `} />
			)}
		</Button>
	);
};

export const RoundButton = (props: CustomButtonProps) => {
	const { className, id, formName = '', ...rest } = props;
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<Button
			{...rest}
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			rounded
			className={classNames('px-6 py-2 font-medium text-lg font-poppins', className)}
		/>
	);
};
// 'MT' - Main Theme
export const PrimaryButtonMT = (props: CustomButtonProps) => {
	return (
		<IconButton
			rounded
			// Beware of the order. Props should be last for user of the component to be able to override them. But, className gets its own override. So, it should follow afterwards. Otherwise it gets wiped by props.
			{...props}
			className={cn('px-6 py-2 h-10 text-white font-medium', props.className)}
		></IconButton>
	)
};
export const PrimaryButtonMTV2 = (props: CustomButtonProps) => {
	return (
		<PrimaryButtonMT
			rounded
			{...props}
			className={cn('py-3 px-10 h-12 ', props.className)}
		></PrimaryButtonMT>
	)
};
export const SecondaryButtonMT = (props: CustomButtonProps) => {
	return (
		<IconButton
			rounded
			{...props}
			className={cn('px-6 py-2 !h-10 text-secondary font-medium bg-secondaryContainer', props.className)}
		></IconButton>
	)
};

export const RejectButtonMT = (props: CustomButtonProps) => {
	return (
		<IconButton
			rounded
			{...props}
			className={cn('bg-transparent text-primary px-1 py-2 text-label-large font-medium', props.className)}
		></IconButton>
	)
};
// TBD. 'CT' - Concierge Theme.  Better to keep closer to concierge folder.
//

export const IconButton = (props: CustomButtonProps) => {
	const { className, id, formName = '', ...rest } = props;
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	const resClass = cn('px-6 py-2 font-medium text-lg font-poppins', className);

	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			{...rest}
			className={resClass}
		/>
	);
};

export const CustomButton = (
	props: CustomButtonProps & ButtonHTMLAttributes<HTMLButtonElement>,
) => {
	const {
		className,
		rounded,
		children,
		id,
		formName = '',
		type = 'button',
		onClick,
	} = props;

	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className={classNames(
				className,
				{
					'rounded-full': rounded,
				},
				'font-poppins',
			)}
			onClick={onClick}
			type={type}
		>
			{children}
		</button>
	);
};

export const ContinueWithGoogleButton = ({
	onClick,
	disabled,
	id,
	formName = '',
}: Props) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);
	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className="continue-with-google-button"
			onClick={onClick}
			disabled={disabled}
			rounded
			outlined
			type="button"
		>
			<img
				width={18}
				height={18}
				src="/images/google_button.svg"
				alt="google-button"
			/>
			<Container className="ml-2">
				<ButtonLabel text={CONTINUE_WITH_GOOGLE} className="text-primary" />
			</Container>
		</Button>
	);
};

export const BackButton = ({
	disabled,
	formName = '',
	id,
	label,
	className,
	onClick,
	type = 'button',
	withIcon,
}: Props) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className={cn('back-button', className)}
			onClick={onClick}
			disabled={disabled}
			type={type}
		>
			{withIcon ? <MdArrowBack size={18} /> : ''}
			<ButtonLabel text={label ?? BACK} />
		</Button>
	);
};

export const NextButton = ({
	onClick,
	withIcon = true,
	label,
	id,
	formName = '',
	type = 'button',
	width = 'w-full',
	disabled = false,
}: Props) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className={`${width} next-button`}
			onClick={onClick}
			type={type}
			disabled={disabled}
		>
			<ButtonLabel text={label ?? NEXT} />
			{withIcon ? <MdArrowForward size={18} /> : ''}
		</Button>
	);
};

export const SecondaryButton = ({
	onClick,
	label,
	id,
	formName = '',
	type = 'button',
	width = 'w-full',
}: Props) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className={classNames('secondary-button', width)}
			onClick={onClick}
			type={type}
		>
			<ButtonLabel text={label ?? 'Next'} />
		</Button>
	);
};

export const GoBackButtonFilled = ({
	onClick,
	withIcon = true,
	label,
	id,
	formName = '',
	type = 'button',
}: Props) => {
	const idService = new IdGeneratorService(formName);
	const idGenerated = isEmpty(formName)
		? idService.generateButtonIds(id)
		: idService.generateFormButtonIds(id);

	return (
		<Button
			id={idGenerated.id}
			data-testid={idGenerated.testId}
			className="go-back-button"
			onClick={onClick}
			type={type}
		>
			{withIcon ? <MdArrowBack size={18} /> : ''}
			<ButtonLabel text={label ?? 'Go Back'} />
		</Button>
	);
};


export const TransparentButton = ({ label, className, onClick }: Omit<Props, 'id'>) => (
	<Button
		label={label}
		onClick={onClick}
		className={cn(
			'h-10 rounded-[100px] px-3 py-2 bg-transparent',
			'font-poppins text-primary text-label-large2',
			className
		)}
	/>
);
